import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, X } from 'lucide-react';
import { Botao, Selo } from './base';
import { BuscaRemota, EntradaSensivel, Rotulo } from './formulario';
import { email, nomePessoa } from '../nucleo/formato';
import type { Convidado } from '../modules/agenda/tipos';
import { focoPerdido } from '../nucleo/foco';
import { ariaErro } from './validacao-local';

/** Teto de convidados (o mesmo do servidor). */
export const MAX_CONVIDADOS = 50;
export const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

/** Corpo da API: o convidado da equipe vai só pelo id (o nome é lido no servidor). */
export function convidadosParaApi(lista: Convidado[]) {
  return lista.map((c) => (c.tipo === 'interno' ? { tipo: 'interno' as const, usuario_id: c.usuario_id } : c.tipo === 'contato' ? { tipo: 'contato' as const, parceiro_id: c.parceiro_id, nome: c.nome, email: c.email } : { tipo: 'externo' as const, email: c.email, nome: c.nome }));
}

/** Rótulo do convidado no modo discreto: nome pelo `nomePessoa`, e-mail pelo `email()`. */
export function rotuloConvidado(c: Convidado) {
  if (c.tipo === 'interno') return nomePessoa(c.nome);
  if (!c.email) return nomePessoa(c.nome ?? '');
  return c.nome ? `${nomePessoa(c.nome)} (${email(c.email)})` : email(c.email);
}

/** Chave estável do convidado (lista e detalhe): pelo id da pessoa ou do contato, e pelo e-mail só no de fora (rcrm4 ROB-B2). */
export const chaveConvidado = (c: Convidado) => (c.tipo === 'interno' ? `u:${c.usuario_id}` : c.tipo === 'contato' ? `p:${c.parceiro_id}` : `e:${c.email.toLowerCase()}`);

/**
 * Lote 19 (12b): lista de convidados com inclusão da equipe (membros) e de contatos de fora (e-mail e nome opcional). Mora em
 * `componentes` porque a tarefa do CRM usa os mesmos campos (tela de módulo não importa tela de outro módulo). Reunião livre
 * (03/11): `aoRascunho` conta o que está digitado e ainda não virou convidado, para quem salva direto não perder o e-mail.
 */
export function CampoConvidados({ idBase, valor, aoMudar, aoRascunho, erroEmail }: { idBase: string; valor: Convidado[]; aoMudar: (v: Convidado[]) => void; aoRascunho?: (r: { email: string; nome: string } | null) => void; erroEmail?: string }) {
  const { t } = useTranslation('agenda');
  const [emailNovo, setEmailNovo] = useState('');
  const [nomeNovo, setNomeNovo] = useState('');
  const [erroProprio, setErro] = useState<string | null>(null);
  // Rodada rrl-1 (VIS-M1): o erro do rascunho vindo de quem salva aparece no próprio campo do e-mail e recebe o foco.
  const erro = erroProprio ?? erroEmail ?? null;
  const cheio = valor.length >= MAX_CONVIDADOS;
  useEffect(() => {
    aoRascunho?.(emailNovo.trim() ? { email: emailNovo.trim(), nome: nomeNovo.trim() } : null);
    // Só quando o texto muda (quem recebe troca a cada render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailNovo, nomeNovo]);
  const incluir = (c: Convidado) => {
    if (cheio || valor.some((x) => chaveConvidado(x) === chaveConvidado(c))) return;
    aoMudar([...valor, c]);
  };
  const incluirExterno = () => {
    const e = emailNovo.trim();
    if (!RE_EMAIL.test(e)) {
      setErro(t('convidados.emailInvalido'));
      return;
    }
    setErro(null);
    incluir({ tipo: 'externo', email: e, nome: nomeNovo.trim() || null });
    setEmailNovo('');
    setNomeNovo('');
  };
  // Rodada rrl-4 (VIS-B1): pelo teclado (`detail` 0), o foco volta à busca da equipe, e não ao que estiver antes do bloco (uma seta na
  // Repetição a mudaria). Pelo ponteiro não: no celular, foco em campo abre o teclado da tela. No efeito de layout, junto com a saída
  // do botão: a camada recupera o foco perdido no setTimeout seguinte, e `recuperarFoco` chegaria depois dela.
  const focarBusca = useRef(false);
  useLayoutEffect(() => {
    if (!focarBusca.current) return;
    focarBusca.current = false;
    if (focoPerdido(document.activeElement, document.body)) document.getElementById(`${idBase}-equipe`)?.focus();
  }, [valor, idBase]);
  const remover = (c: Convidado, peloTeclado: boolean) => {
    focarBusca.current = peloTeclado;
    aoMudar(valor.filter((x) => chaveConvidado(x) !== chaveConvidado(c)));
  };
  // Enter no e-mail ou no nome inclui o convidado (dentro de um <form>, enviaria o formulário em volta).
  const aoTeclar = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (emailNovo.trim()) incluirExterno();
  };
  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-tinta-2">{t('campos.convidados')}</span>
      {valor.length ? (
        <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
          {valor.map((c) => (
            <li key={chaveConvidado(c)} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 grow basis-24 font-medium break-words text-tinta-1">{rotuloConvidado(c)}</span>
              <span className="min-w-min">
                <Selo>{t(`convidados.${c.tipo}`)}</Selo>
              </span>
              <Botao variante="fantasma" tamanho="sm" icone={<X className="size-4" />} aria-label={t('acoes.removerItem', { nome: rotuloConvidado(c) })} onClick={(e) => remover(c, e.detail === 0)} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-tinta-3">{t('convidados.vazio')}</p>
      )}
      {cheio ? (
        <p className="text-sm text-tinta-3">{t('convidados.limite', { max: MAX_CONVIDADOS })}</p>
      ) : (
        <>
          <Rotulo id={`${idBase}-equipe`} rotulo={t('convidados.adicionarEquipe')}>
            <BuscaRemota id={`${idBase}-equipe`} def={{ caminho: '/membros', rotulo: (i) => String(i.nome ?? ''), pessoa: true }} valor={null} aoMudar={(v, item) => v && incluir({ tipo: 'interno', usuario_id: v, nome: String(item?.nome ?? '') })} />
          </Rotulo>
          <div className="flex min-w-0 flex-wrap items-end gap-2">
            <div className="min-w-0 grow basis-48">
              <Rotulo id={`${idBase}-email`} rotulo={t('convidados.emailExterno')} erro={erro ?? undefined}>
                <EntradaSensivel id={`${idBase}-email`} {...ariaErro(`${idBase}-email`, erro)} mascara="email" type="email" maxLength={320} value={emailNovo} onKeyDown={aoTeclar} onChange={(e) => (setEmailNovo(e.target.value), setErro(null))} />
              </Rotulo>
            </div>
            <div className="min-w-0 grow basis-40">
              <Rotulo id={`${idBase}-nome`} rotulo={t('convidados.nomeExterno')}>
                <EntradaSensivel id={`${idBase}-nome`} mascara="nome" maxLength={200} value={nomeNovo} onKeyDown={aoTeclar} onChange={(e) => setNomeNovo(e.target.value)} />
              </Rotulo>
            </div>
            <Botao icone={<UserPlus className="size-4" />} aria-disabled={!emailNovo.trim()} onClick={() => emailNovo.trim() && incluirExterno()}>
              {t('convidados.adicionar')}
            </Botao>
          </div>
        </>
      )}
    </div>
  );
}
